import * as React from "react";
import tw from "twin.macro";
import { styled } from "~/config/stitches";
import type { ButtonProps } from "../Button";

type ButtonGroupProps = {
  children: React.ReactElement<ButtonProps>[];
  tone?: "brandAccent" | "critical";
  link?: boolean;
  color?:
    | "brandAccent"
    | "caution"
    | "clovisNavy"
    | "clovisRed"
    | "critical"
    | "info"
    | "neutral"
    | "positive"
    | "promote";
  variant: "ghost" | "soft" | "solid" | "transparent";
};

const ButtonGroupWrapper = styled("div", {
  ...tw`flex`,

  "& > button": tw`border-neutral-700`,
  "& > button:first-of-type": tw`rounded-r-none`,
  "& > button:last-of-type": tw`rounded-l-none`,
  "& > button:not(:first-of-type):not(:last-of-type)": tw`rounded-none`,

  compoundVariants: [
    {
      css: {
        "& > button": tw`border-white`,
        "& > button:disabled": tw`border-emerald-400`,
      },
      tone: "brandAccent",
      variant: "solid",
    },
    {
      css: {
        "& > button:disabled": tw`border-emerald-300`,
      },
      tone: "brandAccent",
      variant: "soft",
    },
    {
      css: {
        "& > button": tw`border-white`,
      },
      tone: "critical",
      variant: "solid",
    },
    {
      css: {
        "& > button:disabled": tw`border-emerald-200`,
      },
      tone: "brandAccent",
      variant: "ghost",
    },
    {
      css: {
        "& > button:disabled": tw`border-emerald-200`,
      },
      tone: "brandAccent",
      variant: "transparent",
    },
    {
      css: {
        "& > button:disabled": tw`border-critical-500`,
      },
      tone: "critical",
      variant: "solid",
    },
    {
      css: {
        "& > button:disabled": tw`border-critical-400`,
      },
      tone: "critical",
      variant: "soft",
    },
    {
      css: {
        "& > button:disabled": tw`border-critical-200`,
      },
      tone: "critical",
      variant: "ghost",
    },
    {
      css: {
        "& > button:disabled": tw`border-critical-200`,
      },
      tone: "critical",
      variant: "transparent",
    },
    {
      css: {
        "& > button": tw`border-info-500`,
      },
      link: true,
      variant: "solid",
    },
    {
      color: "brandAccent",
      css: {
        "& > button": tw`border-emerald-500`,
      },
      variant: "solid",
    },
    {
      color: "caution",
      css: {
        "& > button": tw`border-caution-500`,
      },
      variant: "solid",
    },
    {
      color: "clovisNavy",
      css: {
        "& > button": tw`border-slate-900`,
      },
      variant: "solid",
    },
    {
      color: "clovisRed",
      css: {
        "& > button": tw`border-rose-500`,
      },
      variant: "solid",
    },
    {
      color: "critical",
      css: {
        "& > button": tw`border-critical-500`,
      },
      variant: "solid",
    },
    {
      color: "info",
      css: {
        "& > button": tw`border-info-500`,
      },
      variant: "solid",
    },
    {
      color: "neutral",
      css: {
        "& > button": tw`border-neutral-500`,
      },
      variant: "solid",
    },
    {
      color: "positive",
      css: {
        "& > button": tw`border-positive-500`,
      },
      variant: "solid",
    },
    {
      color: "promote",
      css: {
        "& > button": tw`border-promote-500`,
      },
      variant: "solid",
    },
  ],

  variants: {
    color: {
      brandAccent: {
        "& > button": tw`border-emerald-500`,
      },
      caution: {
        "& > button": tw`border-caution-500`,
      },
      clovisNavy: {
        "& > button": tw`border-slate-900`,
      },
      clovisRed: {
        "& > button": tw`border-rose-500`,
      },
      critical: {
        "& > button": tw`border-critical-500`,
      },
      info: {
        "& > button": tw`border-info-500`,
      },
      neutral: {
        "& > button": tw`border-neutral-500`,
      },
      positive: {
        "& > button": tw`border-positive-500`,
      },
      promote: {
        "& > button": tw`border-promote-500`,
      },
    },
    link: {
      true: tw``,
    },
    tone: {
      brandAccent: {
        "& > button": tw`border-emerald-500`,
      },
      critical: {
        "& > button": tw`border-critical-500`,
      },
    },
    variant: {
      ghost: {
        "& > button": tw`border-neutral-300 shadow-sm`, // To coincide with Button ghost variant
        "& > button:disabled": tw`border-neutral-200`,
      },
      soft: {
        "& > button:disabled": tw`border-neutral-200`,
      },
      solid: {
        "& > button": tw`border-white`,
        "& > button:disabled": tw`border-neutral-500`,
      },
      transparent: {
        "& > button:disabled": tw`border-neutral-200`,
      },
    },
  },
});

export function ButtonGroup(props: ButtonGroupProps) {
  return (
    <ButtonGroupWrapper
      color={props.color}
      link={props.link}
      tone={props.tone}
      variant={props.variant}
    >
      {React.Children.map(
        props.children,
        (button: React.ReactElement<ButtonProps>) =>
          React.cloneElement(button, {
            color: props.color,
            link: props.link,
            tone: props.tone,
            variant: props.variant,
          })
      )}
    </ButtonGroupWrapper>
  );
}
